import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import {
  Box, Typography, Select, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, SelectChangeEvent, Button, TextField, Pagination, Stack, CircularProgress
} from '@mui/material';

interface TableField {
  cName: string;
  displayName: string;
  statisticsHide: number;
  hideInStatTable: number;
}

interface TableData {
  [key: string]: string | number;
}

interface PharmacyError {
  id: string;
  message: string;
}

const ReportForDirectors: React.FC = () => {
  const [pharmacyIds, setPharmacyIds] = useState<string[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<string>(getCurrentMonth());
  const [tableFields, setTableFields] = useState<TableField[]>([]);
  const [pharmacyData, setPharmacyData] = useState<{ [key: string]: TableData[] }>({});
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const itemsPerPage = 10;

  useEffect(() => {
    document.title = 'Отчеты подчиненных аптек | Zerde';
    document.body.style.overflow = 'hidden';
  }, []);

  useEffect(() => {
    const fetchTableStructure = async () => {
      try {
        const response = await axios.get('https://tbl.zerdegroup.kz/api/table-fields');
        let filteredFields = response.data.filter((field: TableField) => field.hideInStatTable === 0);

        if (!filteredFields.some((field: TableField) => field.cName === 'change_balance_end_of_day')) {
          filteredFields.push({
            cName: 'change_balance_end_of_day',
            displayName: 'Остаток мелочи на конец дня',
            statisticsHide: 0,
            hideInStatTable: 0
          });
        }

        setTableFields(filteredFields);
      } catch (error) {
        console.error('Ошибка при получении структуры таблицы:', error);
      }
    };
    fetchTableStructure();
  }, []);

  useEffect(() => {
    const fetchPharmacyIds = async () => {
      try {
        const username = localStorage.getItem('username');
        const response = await axios.get(`https://tbl.zerdegroup.kz/api/user-pharmacy-ids`, {
          params: { username }
        });

        if (response.data.myPharmacy) {
          const ids = response.data.myPharmacy.split(',').map((id: string) => id.trim());
          setPharmacyIds(ids);
        }
      } catch (error) {
        console.error('Error fetching pharmacy IDs:', error);
      }
    };

    fetchPharmacyIds();
  }, []);

  const [pharmacyErrors, setPharmacyErrors] = useState<PharmacyError[]>([]);

  useEffect(() => {
    const fetchPharmacyData = async () => {
      if (pharmacyIds.length === 0) return;

      setIsLoading(true); // Начало загрузки
      try {
        const newPharmacyData: { [key: string]: TableData[] } = {};
        const newErrors: PharmacyError[] = [];

        await Promise.all(
          pharmacyIds.map(async (pharmacyId) => {
            try {
              const username = `ap${pharmacyId}`;
              const response = await axios.get('https://tbl.zerdegroup.kz/api/statistics-datas', {
                params: {
                  username,
                  month: selectedMonth
                }
              });

              if (Array.isArray(response.data)) {
                if (response.data.length === 0) {
                  newErrors.push({
                    id: pharmacyId,
                    message: 'Данные не найдены'
                  });
                } else {
                  newPharmacyData[pharmacyId] = response.data;
                }
              }
            } catch (error) {
              console.error(`Error fetching data for pharmacy ${pharmacyId}:`, error);
              newErrors.push({
                id: pharmacyId,
                message: 'Ошибка загрузки данных'
              });
            }
          })
        );

        setPharmacyData(newPharmacyData);
        setPharmacyErrors(newErrors);
      } catch (error) {
        console.error('Ошибка при получении данных:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPharmacyData();
  }, [pharmacyIds, selectedMonth]);

  function getCurrentMonth(): string {
    const date = new Date();
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  }

  function getMonthsList(): { value: string; label: string }[] {
    const months = [];
    const currentDate = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleString('ru', { year: 'numeric', month: 'long' });
      months.push({ value, label });
    }
    return months;
  }

  function formatNumber(num: number | string | null | undefined): string {
    if (num === null || num === undefined) {
      return '';
    }

    if (typeof num === 'string') {
      num = parseFloat(num);
      if (isNaN(num)) {
        return '';
      }
    }

    return num.toLocaleString('ru-RU', { maximumFractionDigits: 2 });
  }

  const calculateTotals = (data: TableData[]) => {
    const totals: TableData = {};

    tableFields.forEach(field => {
      if (field.statisticsHide === 0 && field.cName !== 'date') {
        totals[field.cName] = data.reduce((sum, row) => {
          const value = Number(row[field.cName]) || 0;
          return sum + value;
        }, 0);
      }
    });

    return totals;
  };

  const handleMonthChange = (event: SelectChangeEvent<string>) => {
    setSelectedMonth(event.target.value);
  };

  const exportToExcel = (pharmacyId: string) => {
    const data = pharmacyData[pharmacyId];
    if (!data || data.length === 0) return;

    const exportData = data.map(row => {
      const newRow: any = {};
      tableFields.forEach(field => {
        if (field.hideInStatTable === 0) {
          if (field.cName === 'date') {
            newRow[field.displayName] = new Date(row[field.cName]).toLocaleDateString('ru-RU');
          } else {
            newRow[field.displayName] = row[field.cName];
          }
        }
      });
      return newRow;
    });

    const totals = calculateTotals(data);
    const totalsRow: any = { 'Дата': 'Итого:' };
    tableFields.forEach(field => {
      if (field.hideInStatTable === 0 && field.cName !== 'date') {
        totalsRow[field.displayName] = totals[field.cName];
      }
    });
    exportData.push(totalsRow);

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const columnWidths = tableFields
      .filter(field => field.hideInStatTable === 0)
      .map(field => ({ wch: Math.max(field.displayName.length, 15) }));
    worksheet['!cols'] = columnWidths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, `Pharmacy_${pharmacyId}`);

    XLSX.writeFile(workbook, `Pharmacy_${pharmacyId}_${selectedMonth}.xlsx`);
  };

  const exportAllToExcel = async () => {
    try {
      const workbook = XLSX.utils.book_new();

      const exportData: any[] = [];

      pharmacyIds.forEach(pharmacyId => {
        const data = pharmacyData[pharmacyId];
        if (!data || data.length === 0) return;

        exportData.push({
          'Дата': `Аптека №${pharmacyId}`,
          ...Object.fromEntries(
            tableFields
              .filter(field => field.hideInStatTable === 0 && field.cName !== 'date')
              .map(field => [field.displayName, ''])
          )
        });

        const pharmacyExportData = data.map(row => {
          const newRow: any = {};
          tableFields.forEach(field => {
            if (field.hideInStatTable === 0) {
              if (field.cName === 'date') {
                newRow[field.displayName] = new Date(row[field.cName]).toLocaleDateString('ru-RU');
              } else {
                newRow[field.displayName] = row[field.cName];
              }
            }
          });
          return newRow;
        });

        exportData.push(...pharmacyExportData);

        const totals = calculateTotals(data);
        const totalsRow: any = { 'Дата': 'Итого:' };
        tableFields.forEach(field => {
          if (field.hideInStatTable === 0 && field.cName !== 'date') {
            totalsRow[field.displayName] = totals[field.cName];
          }
        });
        exportData.push(totalsRow);

        exportData.push({});
      });

      const worksheet = XLSX.utils.json_to_sheet(exportData);

      const columnWidths = tableFields
        .filter(field => field.hideInStatTable === 0)
        .map(field => ({ wch: Math.max(field.displayName.length, 15) }));
      worksheet['!cols'] = columnWidths;

      XLSX.utils.book_append_sheet(workbook, worksheet, `All_Pharmacies_${selectedMonth}`);

      XLSX.writeFile(workbook, `All_Pharmacies_${selectedMonth}.xlsx`);
    } catch (error) {
      console.error('Ошибка при выгрузке общего отчета:', error);
    }
  };

  const filteredPharmacyIds = pharmacyIds.filter(id =>
    id.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const pageCount = Math.ceil(filteredPharmacyIds.length / itemsPerPage);
  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPageIds = filteredPharmacyIds.slice(startIndex, endIndex);

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    setPage(1);
  }, [searchQuery]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Select
            value={selectedMonth}
            onChange={handleMonthChange}
            sx={{ minWidth: 200, mb: 2 }}
            size='small'
          >
            {getMonthsList().map((month) => (
              <MenuItem key={month.value} value={month.value}>
                {month.label}
              </MenuItem>
            ))}
          </Select>

          <TextField
            placeholder="Поиск аптеки"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ mb: 2, width: 200 }}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Найдено аптек: <br />{filteredPharmacyIds.length}
          </Typography>


          <Button
            variant='contained'
            color='secondary'
            onClick={exportAllToExcel}
            disabled={pharmacyIds.length === 0 || Object.keys(pharmacyData).length === 0}
            sx={{ mb: 4 }}
          >
            Excel общ
          </Button>
        </Box>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {currentPageIds.map((pharmacyId) => (
            <Box key={pharmacyId} sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Аптека №{pharmacyId}
                </Typography>
                {pharmacyErrors.find(error => error.id === pharmacyId) ? (
                  <Typography color="error">
                    {pharmacyErrors.find(error => error.id === pharmacyId)?.message}
                  </Typography>
                ) : (
                  <Button
                    variant="contained"
                    color="success"
                    onClick={() => exportToExcel(pharmacyId)}
                    disabled={!pharmacyData[pharmacyId] || pharmacyData[pharmacyId].length === 0}
                  >
                    Excel
                  </Button>
                )}
              </Box>
              {!pharmacyErrors.find(error => error.id === pharmacyId) && (
                <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {tableFields.map((field) => (
                          <TableCell
                            key={field.cName}
                            sx={{
                              whiteSpace: 'nowrap',
                              backgroundColor: '#1976d2',
                              color: 'white',
                              position: 'sticky',
                              top: 0
                            }}
                          >
                            {field.displayName}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pharmacyData[pharmacyId] && pharmacyData[pharmacyId].map((row, index) => (
                        <TableRow
                          key={index}
                          sx={{ '&:nth-of-type(odd)': { backgroundColor: '#ffffff' } }}
                        >
                          {tableFields.map((field) => (
                            <TableCell
                              key={field.cName}
                              sx={{ whiteSpace: 'nowrap' }}
                            >
                              {field.cName === 'date'
                                ? new Date(row[field.cName]).toLocaleDateString('ru-RU')
                                : formatNumber(row[field.cName])}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                      {pharmacyData[pharmacyId] && pharmacyData[pharmacyId].length > 0 && (
                        <TableRow sx={{ fontWeight: 'bold', backgroundColor: '#1976d2', position: 'sticky', bottom: 0 }}>
                          <TableCell sx={{ color: 'white' }}>Итого:</TableCell>
                          {tableFields.slice(1).map((field) => (
                            <TableCell key={field.cName} sx={{ color: 'white' }}>
                              {calculateTotals(pharmacyData[pharmacyId])[field.cName] !== undefined
                                ? formatNumber(calculateTotals(pharmacyData[pharmacyId])[field.cName])
                                : ''}
                            </TableCell>
                          ))}
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          ))}
          {pageCount > 1 && (
            <Stack spacing={2} alignItems="center" sx={{ mt: 4, mb: 4 }}>
              <Pagination
                count={pageCount}
                page={page}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Stack>
          )}
        </>
      )}
    </Box>
  );
};

export default ReportForDirectors;